/**
 * UserAddPopup
 *
 * Dialog to add a new user and assign their role before their first login.
 * Supports searching users from Azure Active Directory (3-100 chars), manual entry
 * of Name, Email (only Lucy emails), Department, and Role (L1-L9).
 * Enforces duplicate email prevention (case-insensitive) and persists via
 * addUserWithRole API.
 * Dialog to add a new user and assign their role before their first login.
 * Supports searching users from Azure Active Directory (3-100 chars), manual entry
 * of Name, Email (only Lucy emails), Department, and Role (L1-L9).
 * Enforces duplicate email prevention (case-insensitive) and persists via
 * addUserWithRole API.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  getRoles,
  searchDirectoryUsers,
  addUserWithRole,
} from "../../../../api/roles";
import "./user-add-popup.css";

const DUPLICATE_USER_MESSAGE =
  "This user already exists. Please edit the existing user to update their details or role.";

// Helper to extract directory user properties
const extractDirectoryUserId = (u) =>
  u?.directoryUserId || u?.id || u?.userId || u?.objectId || "";

const extractName = (u) => {
  if (u?.displayName) return u.displayName.trim();
  if (u?.name) return u.name.trim();
  const fullName = `${u?.firstName || ""} ${u?.lastName || ""}`.trim();
  return fullName || "";
};

const extractEmail = (u) =>
  (u?.mail || u?.email || u?.emailId || u?.userPrincipalName || "").trim();

const extractDepartment = (u) =>
  (u?.department || u?.departmentName || "").trim();

const parseDirectoryUsersList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data?.users)) return res.data.users;
  if (Array.isArray(res?.users)) return res.users;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data?.users)) return res.data.data.users;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

const isValidLucyEmail = (email) => {
  if (!email || typeof email !== "string") return false;
  // Match standard email format ending with @lucyrx.com or @lucy.com
  const lucyEmailRegex =
    /^[a-z0-9._%+-]+@([a-z0-9-]+\.)*(lucyrx\.com|lucy\.com)$/i;
  return lucyEmailRegex.test(email.trim());
};

const hasResponseErrors = (response) =>
  Boolean(response?.error) ||
  response?.success === false ||
  (Array.isArray(response?.errors) && response.errors.length > 0);

const isDuplicateUserResponse = (response, errorMsg) =>
  response?.status === 409 ||
  (typeof errorMsg === "string" &&
    errorMsg.toLowerCase().includes("already exist"));

const UserAddPopup = ({
  isOpen,
  onClose,
  roles = [],
  existingUsers = [],
  onUserAdded,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [department, setDepartment] = useState("");
  const [roleId, setRoleId] = useState("");
  const [directoryUserId, setDirectoryUserId] = useState("");
  const [selectedDirectoryUser, setSelectedDirectoryUser] = useState(null);

  // Directory Search State
  const [directoryQuery, setDirectoryQuery] = useState("");
  const [directoryResults, setDirectoryResults] = useState([]);
  const [isSearchingDirectory, setIsSearchingDirectory] = useState(false);
  const [showDirectoryDropdown, setShowDirectoryDropdown] = useState(false);
  const [directorySearchError, setDirectorySearchError] = useState("");

  // Form Status State
  const [rolesList, setRolesList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    role: false,
  });

  const searchContainerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Fetch roles if not provided via props
  useEffect(() => {
    let isMounted = true;
    const fetchAvailableRoles = async () => {
      if (Array.isArray(roles) && roles.length > 0) {
        setRolesList(roles);
        return;
      }
      try {
        const res = await getRoles();
        const raw = res?.data ?? res;
        const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
        if (isMounted) {
          const mapped = list
            .filter((r) => (r.roleName || r.name || "").toUpperCase() !== "L0")
            .map((r) => ({
              id: r.roleId ?? r.id,
              name: r.roleName ?? r.name,
            }));
          setRolesList(mapped);
        }
      } catch (err) {
        console.error("Failed to load roles in UserAddPopup", err);
      }
    };

    if (isOpen) {
      void fetchAvailableRoles();
    }
    return () => {
      isMounted = false;
    };
  }, [isOpen, roles]);

  // Filter and sort roles L1–L9
  const filteredRoles = useMemo(() => {
    const list = Array.isArray(rolesList) ? [...rolesList] : [];
    const lRoles = list.filter((r) => {
      const rName = (r?.name || "").trim().toUpperCase();
      return /^L[1-9]$/.test(rName);
    });

    lRoles.sort((a, b) => {
      const numA = Number.parseInt(a.name.replace(/\D/g, ""), 10) || 0;
      const numB = Number.parseInt(b.name.replace(/\D/g, ""), 10) || 0;
      return numA - numB;
    });

    // If no exact L1-L9 matches, fallback to non-L0 roles sorted
    if (lRoles.length === 0) {
      return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    return lRoles;
  }, [rolesList]);

  // Reset form when dialog opens/closes
  const handleResetForm = useCallback(() => {
    setName("");
    setEmail("");
    setDepartment("");
    setRoleId("");
    setDirectoryUserId("");
    setSelectedDirectoryUser(null);
    setDirectoryQuery("");
    setDirectoryResults([]);
    setIsSearchingDirectory(false);
    setShowDirectoryDropdown(false);
    setDirectorySearchError("");
    setFormError("");
    setTouched({ name: false, email: false, role: false });
  }, []);

  useEffect(() => {
    if (isOpen) {
      handleResetForm();
    }
  }, [isOpen, handleResetForm]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setShowDirectoryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check duplicate email (case-insensitive) against existing users
  const isDuplicateEmail = useMemo(() => {
    if (!email || !Array.isArray(existingUsers) || existingUsers.length === 0) {
      return false;
    }
    const normalized = email.trim().toLowerCase();
    return existingUsers.some((u) => {
      const uEmail = (u?.emailId || u?.email || "").trim().toLowerCase();
      return uEmail === normalized;
    });
  }, [email, existingUsers]);

  // Debounced Directory Search
  const handleDirectorySearch = (val) => {
    setDirectoryQuery(val);
    setDirectorySearchError("");

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmed = val.trim();
    if (trimmed.length < 3) {
      setDirectoryResults([]);
      setShowDirectoryDropdown(false);
      setIsSearchingDirectory(false);
      return;
    }

    setIsSearchingDirectory(true);
    setShowDirectoryDropdown(true);

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const res = await searchDirectoryUsers(trimmed);
        if (res?.error) {
          setDirectorySearchError(res.error);
          setDirectoryResults([]);
        } else {
          const list = parseDirectoryUsersList(res);
          setDirectoryResults(list);
          if (list.length === 0) {
            setDirectorySearchError("No matching users found in directory.");
          }
        }
      } catch (err) {
        console.error("Directory search failed", err);
        setDirectorySearchError("Failed to search directory.");
        setDirectoryResults([]);
      } finally {
        setIsSearchingDirectory(false);
      }
    }, 350);
  };

  // Handle selecting a user from directory search
  const handleSelectDirectoryUser = (user) => {
    const uName = extractName(user);
    const uEmail = extractEmail(user);
    const uDept = extractDepartment(user);
    const uId = extractDirectoryUserId(user);

    setName(uName);
    setEmail(uEmail);
    setDepartment(uDept);
    setDirectoryUserId(uId);
    setSelectedDirectoryUser(user);
    setShowDirectoryDropdown(false);
    setDirectorySearchError("");
    setFormError("");
  };

  const handleClearDirectoryUser = () => {
    setSelectedDirectoryUser(null);
    setDirectoryUserId("");
  };

  // Validation
  const isEmailValid = useMemo(() => isValidLucyEmail(email), [email]);
  const isFormValid = useMemo(() => {
    return (
      name.trim().length > 0 &&
      email.trim().length > 0 &&
      isEmailValid &&
      !isDuplicateEmail &&
      Boolean(roleId)
    );
  }, [name, email, isEmailValid, isDuplicateEmail, roleId]);

  const getSubmitValidationError = () => {
    if (!name.trim()) return "Name is mandatory.";
    if (!email.trim()) return "Email is mandatory.";
    if (!isEmailValid) {
      return "Please enter a valid Lucy email address (e.g. username@lucyrx.com).";
    }
    if (isDuplicateEmail) return DUPLICATE_USER_MESSAGE;
    if (!roleId) return "Please select a role.";
    return "";
  };

  // If manual entry and directoryUserId not yet captured, search directory with email
  const resolveDirectoryUserId = async () => {
    if (directoryUserId) return directoryUserId;
    try {
      const queryTerm = email.trim().length >= 3 ? email.trim() : name.trim();
      if (queryTerm.length >= 3 && queryTerm.length <= 100) {
        const searchRes = await searchDirectoryUsers(queryTerm);
        const list = parseDirectoryUsersList(searchRes);

        const matchedUser = list.find(
          (u) => extractEmail(u).toLowerCase() === email.trim().toLowerCase(),
        );

        if (matchedUser) {
          const matchedId = extractDirectoryUserId(matchedUser);
          setDirectoryUserId(matchedId);
          return matchedId;
        }
      }
    } catch (e) {
      console.warn("Directory auto-lookup failed", e);
    }
    return directoryUserId;
  };

  // Submit Handler
  const handleSubmit = async () => {
    setTouched({ name: true, email: true, role: true });
    setFormError("");

    const validationError = getSubmitValidationError();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsSubmitting(true);
    const resolvedDirectoryUserId = await resolveDirectoryUserId();

    if (!resolvedDirectoryUserId) {
      setIsSubmitting(false);
      setFormError(
        "Could not find this user in the directory. Please use the Directory Search above to select a valid directory user.",
      );
      return;
    }

    try {
      const payload = {
        directoryUserId: resolvedDirectoryUserId,
        roleId: Number(roleId),
      };
      const response = await addUserWithRole(payload);

      if (hasResponseErrors(response)) {
        const errorMsg =
          response?.error ||
          response?.message ||
          response?.errors?.[0]?.message ||
          response?.errors?.[0] ||
          "Failed to add user.";

        setFormError(
          isDuplicateUserResponse(response, errorMsg)
            ? DUPLICATE_USER_MESSAGE
            : errorMsg,
        );
        setIsSubmitting(false);
        return;
      }

      // User created successfully
      if (typeof onUserAdded === "function") {
        onUserAdded(response);
      }
      handleResetForm();
      onClose();
    } catch (err) {
      setFormError(err?.message || "Failed to create user. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  let emailHelperText = "";
  if (touched.email && !email.trim()) {
    emailHelperText = "Email is required";
  } else if (touched.email && !isEmailValid) {
    emailHelperText = "Must be a valid Lucy email (e.g. @lucyrx.com)";
  }

  const directoryResultsContent =
    directoryResults.length > 0 ? (
      <List dense disablePadding>
        {directoryResults.map((u) => {
          const uId = extractDirectoryUserId(u);
          const uName = extractName(u);
          const uEmail = extractEmail(u);
          const uDept = extractDepartment(u);

          return (
            <ListItem
              key={uId || uEmail}
              button
              onClick={() => handleSelectDirectoryUser(u)}
              sx={{
                borderBottom: "1px solid #F0F0F0",
                "&:hover": { backgroundColor: "#F7F8FB" },
                cursor: "pointer",
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={600}>
                    {uName || "Unknown Name"}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {uEmail} {uDept ? `• ${uDept}` : ""}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
    ) : (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {directorySearchError || "No users found in directory"}
        </Typography>
      </Box>
    );

  return (
    <Dialog
      open={isOpen}
      maxWidth="sm"
      fullWidth
      className="add-user-popup"
      onClose={(event, reason) => {
        if (reason === "backdropClick" || isSubmitting) return;
        onClose();
      }}
    >
      <DialogTitle className="popup-header">
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <span>Add New User</span>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={isSubmitting}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className="popup-content">
        {/* Error Banners */}
        {isDuplicateEmail && (
          <Alert severity="error" sx={{ mb: 2, fontWeight: 500 }}>
            {DUPLICATE_USER_MESSAGE}
          </Alert>
        )}

        {formError && !isDuplicateEmail && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        {/* Directory Search Section */}
        <Box
          ref={searchContainerRef}
          sx={{ position: "relative", mb: 3, width: "100%" }}
        >
          <InputLabel className="user-label">Search Users</InputLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or email (3–100 characters)..."
            value={directoryQuery}
            onChange={(e) => handleDirectorySearch(e.target.value)}
            onFocus={() => {
              if (directoryResults.length > 0) {
                setShowDirectoryDropdown(true);
              }
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#403C6F" }} />
                  </InputAdornment>
                ),
                endAdornment: isSearchingDirectory ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} sx={{ color: "#403C6F" }} />
                  </InputAdornment>
                ) : null,
              },
            }}
            sx={{
              backgroundColor: "#F7F8FB",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />

          {/* Directory Search Results Dropdown */}
          {showDirectoryDropdown && directoryQuery.trim().length >= 3 && (
            <Paper
              elevation={4}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 1300,
                mt: 0.5,
                maxHeight: 220,
                overflowY: "auto",
                borderRadius: "8px",
                border: "1px solid #E0E0E0",
              }}
            >
              {isSearchingDirectory ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    gap: 1,
                  }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Searching directory...
                  </Typography>
                </Box>
              ) : (
                directoryResultsContent
              )}
            </Paper>
          )}

          {selectedDirectoryUser && (
            <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
              <Chip
                icon={<CheckCircleOutlineIcon fontSize="small" />}
                label={`${extractName(selectedDirectoryUser)} (${extractEmail(selectedDirectoryUser)})`}
                onDelete={handleClearDirectoryUser}
                color="primary"
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#403C6F",
                  color: "#403C6F",
                  "& .MuiChip-icon": { color: "#403C6F" },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Name Field */}
        <Box sx={{ mb: 2.5 }}>
          <InputLabel className="user-label" htmlFor="user-name-input">
            Name <span style={{ color: "red" }}>*</span>
          </InputLabel>
          <TextField
            id="user-name-input"
            fullWidth
            size="small"
            placeholder="Enter full name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setFormError("");
            }}
            onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
            error={touched.name && !name.trim()}
            helperText={touched.name && !name.trim() ? "Name is required" : ""}
            className="user-input"
          />
        </Box>

        {/* Email Field */}
        <Box sx={{ mb: 2.5 }}>
          <InputLabel className="user-label" htmlFor="user-email-input">
            Email <span style={{ color: "red" }}>*</span>
          </InputLabel>
          <TextField
            id="user-email-input"
            fullWidth
            size="small"
            placeholder="e.g. username@lucyrx.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setFormError("");
            }}
            onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
            error={
              (touched.email && !email.trim()) ||
              (touched.email && !isEmailValid) ||
              isDuplicateEmail
            }
            helperText={emailHelperText}
            className="user-input"
          />
        </Box>

        {/* Department Field */}
        <Box sx={{ mb: 2.5 }}>
          <InputLabel className="user-label" htmlFor="user-dept-input">
            Department
          </InputLabel>
          <TextField
            id="user-dept-input"
            fullWidth
            size="small"
            placeholder="Enter department name"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            className="user-input"
          />
        </Box>

        {/* Role Dropdown */}
        <Box sx={{ mb: 1 }}>
          <InputLabel className="user-label" id="user-role-select-label">
            Role <span style={{ color: "red" }}>*</span>
          </InputLabel>
          <FormControl
            fullWidth
            size="small"
            error={touched.role && !roleId}
            className="user-input"
          >
            <Select
              labelId="user-role-select-label"
              id="user-role-select"
              value={roleId}
              displayEmpty
              onChange={(e) => {
                setRoleId(e.target.value);
                setFormError("");
              }}
              onBlur={() => setTouched((prev) => ({ ...prev, role: true }))}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography color="#9E9E9E" fontSize="15px">
                      Select Role (L1–L9)
                    </Typography>
                  );
                }
                const match = filteredRoles.find(
                  (r) => String(r.id) === String(selected),
                );
                return match ? match.name : selected;
              }}
              sx={{
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            >
              <MenuItem value="" disabled>
                Select Role (L1–L9)
              </MenuItem>
              {filteredRoles.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {touched.role && !roleId && (
            <FormHelperText
              error
              sx={{
                color: "#d32f2f !important",
                ml: 1.5,
                mt: "4px",
                fontSize: "12px",
              }}
            >
              Role is required
            </FormHelperText>
          )}
        </Box>
      </DialogContent>

      <DialogActions className="popup-footer">
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          className="secondary"
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          className="primary"
          startIcon={
            isSubmitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {isSubmitting ? "Adding User..." : "Add User"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

UserAddPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  roles: PropTypes.array,
  existingUsers: PropTypes.array,
  onUserAdded: PropTypes.func,
};

export default UserAddPopup;
